import { AxiosError } from 'axios';
import { ValidationError } from 'yup';
import { BackendErrorList } from '../types';

export const getSafe = <T>(obj: T | undefined, errorMessage = `Object is not defined, but it's required`): T => {
  if (obj === undefined) {
    throw new Error(errorMessage);
  }
  return obj;
};

/**
 * Removes undefined and null values from an object
 * @param obj object to clean
 * @example
 *
 * const obj = {
 *   ric: 'OTPB.BU',
 *   low: undefined,
 *   high: 3245,
 *   closePrice: null,
 * };
 *
 * cleanObject(obj) // { ric: 'OTPB.BU', high: 3245 }
 */
export const cleanObject = <T extends { [key: string]: any }>(obj: Partial<T>): Partial<T> => {
  Object.keys(obj).forEach(key => {
    if (obj[key] === undefined || obj[key] === null) {
      delete obj[key];
    }
  });

  return obj;
};

/**
 * Merges the given object into the target object. If a field in the from object is undefined or null, it will be removed from the target object.
 * @param target
 * @param from
 * @param nullableFields
 */
export const mergeObjectInto = <T>(target: T, from: Partial<T>, nullableFields: Array<keyof T> = []): T => {
  const res = target ? { ...target } : ({} as T);
  const keys = Object.keys(from);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i] as keyof T;
    const value = from[key];
    if (value === null && nullableFields.includes(key)) {
      delete res[key];
    } else if (value !== undefined && value !== null) {
      res[key] = value as T[keyof T];
    }
  }

  return res;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
};

/**
 * Merges the given object into the target object, but only fills in fields that are undefined or null in the target.
 * Existing values in the target are preserved. Useful for REST snapshot data that should not overwrite newer streaming values.
 *
 * For plain object fields (like `details`, `fundamentals`), the merge is applied recursively per-key,
 * so individual sub-fields can be filled in without overwriting existing ones.
 *
 * @param target
 * @param from
 */
export const mergeSnapshotInto = <T>(target: T, from: Partial<T>): T => {
  const res = target ? { ...target } : ({} as T);
  const keys = Object.keys(from);

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i] as keyof T;
    const fromValue = from[key];
    const targetValue = res[key];

    if (fromValue === undefined || fromValue === null) {
      continue;
    }

    if (targetValue === undefined || targetValue === null) {
      res[key] = fromValue as T[keyof T];
    } else if (isPlainObject(targetValue) && isPlainObject(fromValue)) {
      res[key] = mergeSnapshotInto(
        targetValue as Record<string, unknown>,
        fromValue as Record<string, unknown>,
      ) as T[keyof T];
    }
    // existing non-null, non-object value: keep target (no overwrite)
  }

  return res;
};

/**
 * Checks if the given object is empty (has no keys)
 */
export const isEmpty = <T extends {}>(obj: T): boolean => {
  return Object.keys(obj).length === 0;
};

/**
 * Checks if the given object is defined. For undefined and null it will return false, otherwise true.
 */
export const isDefined = <T>(obj: T | undefined | null): obj is T => {
  return obj !== undefined && obj !== null;
};

/**
 * Checks if the given error is an AxiosError
 */
export const isAxiosError = (error: any): error is AxiosError => {
  return (error as AxiosError).isAxiosError || false;
};

export const isNetworkError = (error: any): boolean => {
  return (error as AxiosError).code === 'ERR_NETWORK' || (error as AxiosError).message === 'Network Error';
};

export const isBackendErrorList = (data: any): data is BackendErrorList => {
  return isDefined((data as BackendErrorList)?.errors) && Array.isArray((data as BackendErrorList).errors);
};

/**
 * Checks if the given error is a Yup validation error and has a response
 */

export const isYupValidationError = (error: any): error is ValidationError => {
  return (error as ValidationError).inner !== undefined;
};

export const deepEqual = <T>(a: T | undefined, b: T | undefined, exclude?: Array<keyof T>): boolean => {
  if (a === b) return true;

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false;
  }

  const keysA = Object.keys(a).filter(k => !exclude?.includes(k as keyof T)) as (keyof T)[];
  const keysB = Object.keys(b).filter(k => !exclude?.includes(k as keyof T)) as (keyof T)[];

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key)) return false;
    if (!deepEqual(a[key], b[key])) return false;
  }

  return true;
};

/**
 * Utility function to more readable ternaries.
 *
 * <example>
 *   // instead of this
 *   const value = age > 30 ? 'adult' : (age > 18 ? 'young adult' : (age > 12 ? 'teenager' : 'child'));
 *
 *   // use this
 *   const value = when(age > 30, 'adult')
 *     .or(age > 18, 'young adult')
 *     .or(age > 12, 'teenager')
 *     .or(age > 0, 'child')
 *     .get();
 * </example>
 *
 * @param condition A boolean condition. When evaluating to true, the value will be returned.
 * @param value The value to return when the condition is true.
 */
export const when = <T>(condition: boolean | undefined | null | string | number, value: T) => {
  let result = condition ? value : undefined;
  const obj = {
    get: () => {
      return result;
    },
    or: (pCondition: boolean, pValue: T) => {
      result = !condition && pCondition ? pValue : result;
      return obj;
    },
    otherwise: (pValue: T) => {
      result = result === undefined ? pValue : result;
      return obj;
    },
  };
  return obj;
};

/**
 * When is this useful?
 *
 * ```<SomeComponent onSomethingIDontCareAboutButTheCallbackIsMandatory={noop} />```
 *
 * ```
 * useEffect(() => {
 *   if (someCondition) {
 *     return () => cleanup();
 *   }
 *   return noop;
 * }, [someCondition, cleanup])
 * ```
 */
export const noop = () => {};

/**
 * When is this useful?
 *
 * In some cases you need a transformation function (eg.: for Array.map), but in some cases you don't.
 *
 * ```
 * const newValues = values.map(someCondition ? (value) => value + 1 : identity);
 * ```
 */
export const identity = <T>(value: T) => value;

/**
 * Basic referential equality check. Used for default values, when you want an option for equality check,
 * but you need a reasonable default.
 */
export const isStrictEqual = (a: unknown, b: unknown) => a === b;
